import { Icon } from '@chakra-ui/react';
import { BasePagination, IconButtonWithTooltip, PlusIcon } from '@elkaso-app/web-design';
import { useGetCreditUtilizationsApi } from 'apis/bnpl';
import { CreditUtilizationDto } from 'apis/bnpl';
import { BaseTable, BaseTableContainer } from 'components/base-table';
import {
  BaseTableSpinner,
  BaseTableTitle,
  ClearFilterButton,
  CustomTableCell,
  DateTimeCell,
  IDateTimeCell,
  TableCell,
  TableHeader,
} from 'components/base-table/components';
import { Can } from 'configs/can';
import { useSetPaginationParamsV1 } from 'hooks/use-set-pagination-params-v1';
import { BnplStatusBadge } from 'pages/bnpl/components/bnpl-status-badge';
import { CreateCreditUtilizationModal } from 'pages/bnpl/components/create-credit-utilization-modal';
import { formatAmount } from 'pages/bnpl/utils/bnpl-statuses';
import React, { useEffect } from 'react';
import { FiEye } from 'react-icons/fi';
import { useHistory } from 'react-router-dom';
import { CellProps } from 'react-table';
import { changeTitle } from 'redux/actions';
import { useAppDispatch } from 'redux/hooks';

export const CreditUtilizationsListScreen = () => {
  const setPaginationParams = useSetPaginationParamsV1();
  const dispatch = useAppDispatch();
  const history = useHistory();
  const { isLoading, isFetching, data } = useGetCreditUtilizationsApi();

  const { pageCount, total, data: utilizationsData } = data ?? {};

  useEffect(() => {
    dispatch(changeTitle('BNPL - Credit Utilizations'));
  }, []);

  interface IColumnsAccessorData {
    id: number;
    restaurant: { id: number; nameEn: string } | undefined;
    supplier: { id: number; name: string } | undefined;
    sourceType: string;
    amount: number;
    utilizationStatus: string;
    createdAt: string;
    actions: CreditUtilizationDto;
  }

  const columns = React.useMemo(
    () => [
      {
        Header: <TableHeader name='ID' />,
        accessor: 'id',
        Cell: ({ value }: CellProps<any, IColumnsAccessorData['id']>) => <TableCell value={value} />,
      },
      {
        Header: <TableHeader name='Restaurant' />,
        accessor: 'restaurant',
        Cell: ({ value }: CellProps<any, IColumnsAccessorData['restaurant']>) => (
          <TableCell value={value?.nameEn || '---'} fontWeight='semibold' color='gray.600' />
        ),
      },
      {
        Header: <TableHeader name='Supplier' />,
        accessor: 'supplier',
        Cell: ({ value }: CellProps<any, IColumnsAccessorData['supplier']>) => (
          <TableCell value={value?.name || '---'} />
        ),
      },
      {
        Header: <TableHeader name='Source Type' />,
        accessor: 'sourceType',
        Cell: ({ value }: CellProps<any, IColumnsAccessorData['sourceType']>) => (
          <TableCell value={value === 'invoice' ? 'Invoice' : 'Statement'} />
        ),
      },
      {
        Header: <TableHeader name='Amount' />,
        accessor: 'amount',
        Cell: ({ value }: CellProps<any, IColumnsAccessorData['amount']>) => (
          <TableCell value={formatAmount(value)} />
        ),
      },
      {
        Header: <TableHeader name='Status' />,
        accessor: 'utilizationStatus',
        Cell: ({ value }: CellProps<any, IColumnsAccessorData['utilizationStatus']>) => (
          <BnplStatusBadge status={value} type='utilization' />
        ),
      },
      {
        Header: <TableHeader name='Created' />,
        accessor: 'createdAt',
        Cell: ({ value }: CellProps<any, IColumnsAccessorData['createdAt']>) => (
          <CustomTableCell<IDateTimeCell> as={DateTimeCell} date={value} />
        ),
      },
      {
        Header: <TableHeader name='Actions' />,
        accessor: 'actions',
        Cell: ({ value }: CellProps<any, IColumnsAccessorData['actions']>) => (
          <IconButtonWithTooltip
            size='sm'
            label='View Details'
            aria-label='view-details-btn'
            Icon={<Icon as={FiEye} />}
            variant='unstyled'
            onClick={() => history.push(`/app/bnpl/credit-utilizations/${value?.id}`)}
          />
        ),
      },
    ],
    []
  );

  const columnsAccessorData = React.useMemo(
    () =>
      utilizationsData?.map((utilization): IColumnsAccessorData => {
        return {
          id: utilization?.id,
          restaurant: utilization?.creditFacility?.restaurant,
          supplier: utilization?.creditFacility?.supplier,
          sourceType: utilization?.sourceType,
          amount: utilization?.amount,
          utilizationStatus: utilization?.utilizationStatus,
          createdAt: utilization?.createdAt,
          actions: utilization,
        };
      }),
    [utilizationsData]
  );

  return (
    <BaseTableContainer isLoading={isLoading}>
      <BaseTableContainer.Header>
        <BaseTableContainer.Header.Left>
          <BaseTableTitle title='Credit Utilizations' />
          <BaseTableSpinner isFetching={isFetching} />
        </BaseTableContainer.Header.Left>

        <BaseTableContainer.Header.Right>
          <ClearFilterButton />

          <Can I='manage' a='CreditUtilization'>
            <CreateCreditUtilizationModal
              trigger={
                <IconButtonWithTooltip
                  label='Create credit utilization'
                  aria-label='create-credit-utilization-btn'
                  Icon={<PlusIcon />}
                />
              }
            />
          </Can>
        </BaseTableContainer.Header.Right>
      </BaseTableContainer.Header>

      <BaseTableContainer.TableWrapper>
        <BaseTable columns={columns} data={columnsAccessorData} />
      </BaseTableContainer.TableWrapper>

      <BaseTableContainer.Footer>
        <BaseTableContainer.Footer.Left />
        <BaseTableContainer.Footer.Right>
          <BasePagination>
            <BasePagination.Pages pagesCount={pageCount ?? 0} setSearchParams={setPaginationParams} />
            <BasePagination.Limit total={total ?? 0} setSearchParams={setPaginationParams} />
          </BasePagination>
        </BaseTableContainer.Footer.Right>
      </BaseTableContainer.Footer>
    </BaseTableContainer>
  );
};
